// Markdown helpers that work on the source text only (no DOM), so they can run anywhere and be unit-tested.

/** A one-line plain-text version for note list previews. */
export function markdownToPlain(source: string): string {
  return source
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]\s+\[[ xX]\]|[-*+]|\d+[.)])\s+/gm, "")
    .replace(/(\*\*|__|~~|`)/g, "")
    .replace(/(^|\W)[*_](\S[^*_]*)[*_](?=\W|$)/g, "$1$2")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Tick or untick the nth task-list item ("- [ ] …") in the source, counting the same way the
 * rendered checkboxes appear. Items inside fenced code blocks aren't real tasks and are skipped.
 */
export function toggleTask(source: string, index: number): string {
  let seen = 0;
  let inFence = false;
  return source
    .split("\n")
    .map((line) => {
      if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
      if (inFence) return line;
      const m = line.match(/^(\s*(?:>\s*)*[-*+]\s+)\[([ xX])\]/);
      if (!m) return line;
      if (seen++ !== index) return line;
      const checked = m[2] !== " ";
      return `${m[1]}[${checked ? " " : "x"}]${line.slice(m[0].length)}`;
    })
    .join("\n");
}

/**
 * Markdown → readable plain text that keeps the line structure (for .txt export and messages):
 * headings and emphasis lose their markers, links become "text (url)", bullets become "•",
 * task items keep [ ] / [x], code blocks keep their content.
 */
export function markdownToText(source: string): string {
  let inFence = false;
  return source
    .replace(/\r\n/g, "\n")
    .split("\n")
    .flatMap((line) => {
      if (/^\s*(```|~~~)/.test(line)) {
        inFence = !inFence;
        return [];
      }
      if (inFence) return [line];
      return [
        line
          .replace(/<[^>]*>/g, "")
          .replace(/^(\s*)(?:>\s?)+/, "$1")
          .replace(/^(\s*)#{1,6}\s+/, "$1")
          .replace(/^(\s*)[-*+]\s+\[([ xX])\]\s+/, (_, sp: string, c: string) => `${sp}[${c.trim() ? "x" : " "}] `)
          .replace(/^(\s*)[-*+]\s+/, "$1• ")
          .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
          .replace(/\[([^\]]+)\]\(([^)\s]+)[^)]*\)/g, (_, text: string, url: string) => (text === url ? url : `${text} (${url})`))
          .replace(/(\*\*|__|~~)(.+?)\1/g, "$2")
          .replace(/(^|[^\w*])[*_]([^*_\s][^*_]*?)[*_](?=[^\w*]|$)/g, "$1$2")
          .replace(/`([^`]+)`/g, "$1")
          .replace(/^\s*([-*_])(\s*\1){2,}\s*$/, "————")
          .trimEnd(),
      ];
    })
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
